import React, { useState, useEffect, useMemo } from 'react';
import useDataVersion from '../hooks/useDataVersion';
import { Search, Building, Eye, FileText, FileCheck, Files, ExternalLink, X, Printer, Maximize2 } from 'lucide-react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LAST_DAY_OF_MONTH = (y, m) => new Date(y, m, 0).getDate(); // m is 1-indexed month

const API = process.env.REACT_APP_BACKEND_URL || '';

const GUIDE = {
  sections: [
    { title: 'Buyer Ranking', desc: 'Volume-weighted FOB ₹/Kg each buyer pays on the current filter selection. Bars are ranked highest first — higher is better for Sharat.', example: 'Example: a buyer at the top of the list is paying a premium rate relative to others in the same period.' },
    { title: 'Price Trend', desc: 'Each bubble is a single invoice line item, plotted by date and FOB ₹/Kg, sized by shipment volume and colored by buyer. Use the fullscreen icon to inspect closely.', example: 'Example: a cluster of large bubbles at a low rate signals a high-volume, low-margin buyer.' },
    { title: 'Filters', desc: 'Search, period, consignee and market (USA/Russia) filters apply to both the table and the analytics panels above it.', example: '' },
    { title: 'View Docs', desc: 'Opens the Commercial Invoice and Proforma Invoice (PI) side by side with shipment references, line items and totals.', example: '' },
  ],
  lookFor: 'Buyers paying above the overall average FOB rate (amber figure) are the most valuable accounts — watch for consignees consistently below it.',
};

// Country badge colors
const countryBadgeClass = (country) => {
  if ((country || '').toUpperCase() === 'USA') return 'bg-[rgba(69,170,242,0.12)] text-dash-blue';
  return 'bg-[rgba(107,203,119,0.12)] text-dash-green';
};

// Format weight display (adds LBS conversion for USD invoices where applicable)
const formatWeight = (wt, currency) => {
  if (wt == null) return '--';
  const kg = Number(wt);
  const kgStr = kg.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  // Show LBS only for USA invoices where net_weight matches known LBS conversions
  if (currency === 'USD') {
    if (Math.abs(kg - 16344) < 0.1) return `${kgStr} KGS (36,000 LBS)`;
    if (Math.abs(kg - 17229.3) < 0.5) return `${kgStr} KGS (37,950 LBS)`;
  }
  return `${kgStr} KGS`;
};

// Parse dd.mm.yyyy -> Date
const parseDate = (s) => {
  if (!s) return null;
  const [d, m, y] = s.split('.').map(n => parseInt(n, 10));
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

/* ── Skeleton (loading state) — mirrors DashboardSkeleton.jsx's shimmer-bone
   convention, laid out to match this page's own header/filter/analytics/table shape ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

const InvoicePISkeleton = () => (
  <div className="min-h-full bg-dash-bg text-dash-ink font-grotesk antialiased" data-testid="invoice-pi-skeleton">
    <div className="max-w-[1600px] mx-auto p-6 space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-dash-panel p-6 rounded-xl border border-dash-line shadow-sm">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <SB className="h-4 w-44 !rounded" delay={0} />
            <SB className="h-2.5 w-36" delay={20} />
          </div>
          <SB className="h-5 w-64 mb-2" delay={40} />
          <SB className="h-3 w-96" delay={60} />
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <SB className="h-2 w-14 mb-1.5 ml-auto" delay={80} />
            <SB className="h-5 w-16 ml-auto" delay={100} />
          </div>
          <SB className="h-9 w-32 !rounded-lg" delay={120} />
        </div>
      </div>

      {/* Period Filter */}
      <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm flex items-center gap-2.5">
        <SB className="h-2.5 w-12" delay={140} />
        <SB className="h-8 w-28 !rounded-md" delay={160} />
        <SB className="h-2.5 w-4" delay={170} />
        <SB className="h-8 w-28 !rounded-md" delay={180} />
        <SB className="h-8 w-16 !rounded-md" delay={200} />
        <SB className="h-2.5 w-48 ml-auto" delay={220} />
      </div>

      {/* Analytics Dashboard Grid: Buyer Ranking & Price Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Buyer Ranking */}
        <div
          className="lg:col-span-5 rounded-xl p-6"
          style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}
        >
          <SB className="h-4 w-40 mb-2" delay={240} />
          <SB className="h-2.5 w-full mb-1" delay={260} />
          <SB className="h-2.5 w-4/5 mb-5" delay={280} />
          <div className="space-y-3.5">
            {[92, 78, 64, 50, 38, 26].map((w, i) => (
              <div key={i} className="flex items-center gap-3">
                <SB className="h-3 w-28" delay={300 + i * 30} />
                <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ background: 'var(--skel-border)' }}>
                  <SB className="h-full !rounded-none" style={{ width: `${w}%` }} delay={310 + i * 30} />
                </div>
                <SB className="h-3 w-16" delay={320 + i * 30} />
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 flex justify-between items-center" style={{ borderTop: '1px solid var(--skel-border)' }}>
            <SB className="h-2.5 w-24" delay={520} />
            <SB className="h-4 w-28" delay={530} />
          </div>
        </div>

        {/* Price Trend */}
        <div
          className="lg:col-span-7 rounded-xl p-6"
          style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}
        >
          <div className="flex justify-between items-baseline mb-2">
            <SB className="h-4 w-36" delay={260} />
            <SB className="h-3 w-24" delay={280} />
          </div>
          <SB className="h-2.5 w-3/4 mb-4" delay={300} />
          <div className="h-64 relative overflow-hidden rounded-lg" style={{ background: 'var(--dash-bg)' }}>
            {[
              { x: '15%', y: '60%' }, { x: '25%', y: '40%' }, { x: '35%', y: '55%' },
              { x: '45%', y: '30%' }, { x: '55%', y: '48%' }, { x: '65%', y: '35%' },
              { x: '75%', y: '52%' }, { x: '85%', y: '42%' }, { x: '50%', y: '65%' },
              { x: '30%', y: '25%' },
            ].map((b, i) => (
              <div key={i} className="skel-bone skel-round absolute" style={{ left: b.x, top: b.y, width: 18, height: 18, animationDelay: `${340 + i * 40}ms` }} />
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            {[0, 1, 2].map(i => <SB key={i} className="h-2.5 w-20" delay={700 + i * 30} />)}
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
        <SB className="h-9 w-full lg:w-80 !rounded-lg" delay={760} />
        <div className="flex flex-wrap items-center gap-3">
          <SB className="h-8 w-36 !rounded-lg" delay={780} />
          <SB className="h-8 w-40 !rounded-lg" delay={800} />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
        <div className="px-4 py-3 flex gap-6 items-center" style={{ background: 'var(--skel-border)' }}>
          {[28, 32, 20, 16, 16, 24, 10].map((w, i) => <SB key={i} className="h-2.5" style={{ width: `${w * 3}px` }} delay={820 + i * 15} />)}
        </div>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} className="px-4 py-3.5 flex gap-6 items-center" style={{ borderTop: '1px solid var(--skel-border)' }}>
            <SB className="h-3 w-24" delay={900 + i * 40} />
            <SB className="h-3 w-32" delay={910 + i * 40} />
            <SB className="h-3 w-20" delay={920 + i * 40} />
            <SB className="h-3 w-16" delay={930 + i * 40} />
            <SB className="h-3 w-16" delay={940 + i * 40} />
            <SB className="h-3 w-24" delay={950 + i * 40} />
            <SB className="h-6 w-20 ml-auto !rounded-lg" delay={960 + i * 40} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Tooltip renderer for the price trend scatter chart
const PriceTrendTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-dash-panel border border-dash-line rounded-lg p-3 text-xs text-dash-ink shadow-2xl min-w-[220px]">
      <div className="font-bold text-dash-ink mb-1">{d.invoice_no}</div>
      <div className="text-dash-mut">{d.consignee}</div>
      <div className="text-dash-mut mt-1.5 text-[11px] leading-tight">{d.description}</div>
      <div className="border-t border-dash-line mt-2 pt-1.5 flex justify-between">
        <span className="text-dash-mut">Date:</span>
        <span className="font-mono">{d.date}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-dash-mut">Rate:</span>
        <span className="font-mono text-dash-amber font-bold">₹{d.price_inr_per_kg}/KG</span>
      </div>
      <div className="flex justify-between">
        <span className="text-dash-mut">Volume:</span>
        <span className="font-mono">{d.volume_kg.toLocaleString('en-IN')} KGS</span>
      </div>
    </div>
  );
};

const InvoicePITrackerPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [analytics, setAnalytics] = useState({ buyer_ranking: [], overall_avg_fob: 0, price_trend: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [consigneeFilter, setConsigneeFilter] = useState('all');
  const [countryTab, setCountryTab] = useState('all');

  // Period filter — months derived from the actual invoice dates present, same
  // month-index band pattern (fromIdx/toIdx + apply/reset) as the other dashboards' PeriodFilter.
  const [months, setMonths] = useState([]); // [{ label: 'Apr-2025', year, month }]
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [appliedRange, setAppliedRange] = useState([0, 0]);
  const [isFiltered, setIsFiltered] = useState(false);

  // Modal state
  const [selected, setSelected] = useState(null);
  const [docTab, setDocTab] = useState('invoice'); // 'invoice' | 'proforma'

  // Fullscreen for Price Trend chart
  const [trendFullscreen, setTrendFullscreen] = useState(false);

  // Derive the applied period's [date_from, date_to] as yyyy-mm-dd, or null when unfiltered
  const appliedDateRange = useMemo(() => {
    if (!isFiltered || months.length === 0) return { dateFrom: null, dateTo: null };
    const from = months[appliedRange[0]];
    const to = months[appliedRange[1]];
    if (!from || !to) return { dateFrom: null, dateTo: null };
    const dateFrom = `${from.year}-${String(from.month).padStart(2, '0')}-01`;
    const dateTo = `${to.year}-${String(to.month).padStart(2, '0')}-${String(LAST_DAY_OF_MONTH(to.year, to.month)).padStart(2, '0')}`;
    return { dateFrom, dateTo };
  }, [isFiltered, months, appliedRange]);

  // Build query string for filters (shared by list & analytics)
  const buildQuery = () => {
    const params = new URLSearchParams();
    if (countryTab !== 'all') params.set('country', countryTab);
    if (consigneeFilter !== 'all') params.set('consignee', consigneeFilter);
    if (appliedDateRange.dateFrom) params.set('date_from', appliedDateRange.dateFrom);
    if (appliedDateRange.dateTo) params.set('date_to', appliedDateRange.dateTo);
    return params.toString();
  };

  // Fetch invoices + consignees (once)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [invRes, cnRes] = await Promise.all([
          fetch(`${API}/api/invoice-pi`, { credentials: 'include' }),
          fetch(`${API}/api/invoice-pi/consignees`, { credentials: 'include' }),
        ]);
        if (!invRes.ok) throw new Error(`Failed to load invoices (${invRes.status})`);
        const invData = await invRes.json();
        const cnData = cnRes.ok ? await cnRes.json() : { consignees: [] };
        const invList = invData.invoices || [];
        setInvoices(invList);
        setConsignees(cnData.consignees || []);

        // Build the chronological list of distinct invoice months
        const seen = new Map();
        invList.forEach((r) => {
          const d = parseDate(r.date);
          if (!d) return;
          const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
          if (!seen.has(key)) seen.set(key, { year: d.getFullYear(), month: d.getMonth() + 1 });
        });
        const mm = [...seen.values()]
          .sort((a, b) => (a.year - b.year) || (a.month - b.month))
          .map((m) => ({ ...m, label: `${MONTH_ABBR[m.month - 1]}-${m.year}` }));
        const last = Math.max(0, mm.length - 1);
        setMonths(mm);
        setToIdx(last);
        setAppliedRange([0, last]);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Auto-refresh when admin publishes fresh invoice data
  useDataVersion('invoice-pi', () => {
    // Re-run the initial load
    (async () => {
      try {
        const [invRes, cnRes] = await Promise.all([
          fetch(`${API}/api/invoice-pi`, { credentials: 'include' }),
          fetch(`${API}/api/invoice-pi/consignees`, { credentials: 'include' }),
        ]);
        const invData = await invRes.json();
        const cnData = cnRes.ok ? await cnRes.json() : { consignees: [] };
        setInvoices(invData.invoices || []);
        setConsignees(cnData.consignees || []);
      } catch { /* silent */ }
    })();
  }, { toastLabel: 'Invoice data' });

  const applyPeriod = () => {
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setAppliedRange([fromIdx, toIdx]);
    setIsFiltered(!(fromIdx === 0 && toIdx === months.length - 1));
  };

  const resetPeriod = () => {
    const last = Math.max(0, months.length - 1);
    setFromIdx(0); setToIdx(last);
    setAppliedRange([0, last]);
    setIsFiltered(false);
  };

  const periodLabel = months.length === 0 ? '' : isFiltered
    ? `${months[appliedRange[0]].label} to ${months[appliedRange[1]].label}`
    : `All ${months.length} month${months.length === 1 ? '' : 's'} · ${months[0].label} to ${months[months.length - 1].label}`;

  // Fetch analytics whenever filters change
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const qs = buildQuery();
        const url = `${API}/api/invoice-pi/analytics${qs ? '?' + qs : ''}`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAnalytics(data);
        }
      } catch (e) {
        // silently ignore analytics failure
      }
    };
    loadAnalytics();
  }, [countryTab, consigneeFilter, appliedDateRange.dateFrom, appliedDateRange.dateTo]);

  const handlePrint = () => {
    window.print();
  };

  // Filter invoices
  const filtered = useMemo(() => {
    let out = invoices;

    if (countryTab !== 'all') {
      out = out.filter(r => (r.country || '').toLowerCase() === countryTab.toLowerCase());
    }

    if (consigneeFilter !== 'all') {
      out = out.filter(r => r.consignee === consigneeFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        (r.invoice_no || '').toLowerCase().includes(q) ||
        (r.consignee || '').toLowerCase().includes(q) ||
        (r.port || '').toLowerCase().includes(q) ||
        (r.payment_terms || '').toLowerCase().includes(q)
      );
    }

    // Period filter (applied month-index range)
    if (isFiltered && appliedDateRange.dateFrom && appliedDateRange.dateTo) {
      const from = new Date(appliedDateRange.dateFrom);
      const to = new Date(appliedDateRange.dateTo);
      out = out.filter(r => {
        const d = parseDate(r.date);
        if (!d) return false;
        return d >= from && d <= to;
      });
    }

    return out;
  }, [invoices, countryTab, consigneeFilter, search, isFiltered, appliedDateRange]);

  const openModal = (inv) => {
    setSelected(inv);
    setDocTab('invoice');
  };

  const closeModal = () => {
    setSelected(null);
  };

  // ESC to close modal or fullscreen chart
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (trendFullscreen) setTrendFullscreen(false);
        else closeModal();
      }
    };
    if (selected || trendFullscreen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, trendFullscreen]);

  const pdfUrl = useMemo(() => {
    if (!selected) return null;
    const path = docTab === 'invoice' ? selected.invoice_pdf_url : selected.proforma_pdf_url;
    return path ? `${API}${path}` : null;
  }, [selected, docTab]);

  if (loading) return <InvoicePISkeleton />;

  return (
    <div className="min-h-full bg-dash-bg text-dash-ink font-grotesk antialiased">
      <div className="max-w-[1600px] mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-dash-panel p-6 rounded-xl border border-dash-line shadow-sm">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bg-[rgba(69,170,242,0.12)] text-dash-blue text-xs font-bold px-2.5 py-0.5 rounded uppercase tracking-wider">Seafood Export Division</span>
              <span className="text-xs text-dash-mut">GSTN: 37AACCS8215J1Z5</span>
            </div>
            <h1 className="text-2xl font-bold text-dash-ink mt-1">Sharat Industries Limited</h1>
            <p className="text-sm text-dash-mut">Export Invoice Management, Price Benchmark & Payment Terms Analytics</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] text-dash-mut uppercase tracking-wider">Invoices</div>
              <div className="text-xl font-bold text-dash-ink">
                {filtered.length}<span className="text-dash-mut text-sm font-medium">/{invoices.length}</span>
              </div>
            </div>
            <button
              onClick={handlePrint}
              className="bg-dash-panel2 hover:bg-dash-line text-dash-ink font-medium px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 print:hidden"
              data-testid="btn-print-summary"
            >
              <Printer className="w-4 h-4" />
              Print Summary
            </button>
          </div>
        </div>

        {/* Period Filter */}
        {months.length > 0 && (
          <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm print:hidden">
            <PeriodFilter
              months={months.map(m => m.label)}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
            />
          </div>
        )}

        {/* Analytics Dashboard Grid: Buyer Ranking & Price Trend */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 print:hidden">
          {/* Buyer Ranking by FOB ₹/Kg */}
          <div className="lg:col-span-5 bg-dash-panel2 text-dash-ink p-6 rounded-xl border border-dash-line shadow-lg flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-baseline mb-1">
                <h2 className="text-lg font-bold text-dash-ink tracking-tight">
                  Buyer Ranking <em className="text-dash-amber font-serif italic font-normal">by FOB ₹/Kg</em>
                </h2>
                <span className="text-[10px] text-dash-mut uppercase tracking-wider font-semibold">Volume Weighted</span>
              </div>
              <p className="text-xs text-dash-mut mb-5">
                Volume-weighted normalized price each buyer pays on current selection. Higher is better for Sharat.
              </p>

              <div className="space-y-3.5">
                {(() => {
                  const rows = analytics.buyer_ranking || [];
                  const top = rows.length > 0 ? rows[0].avg_fob_inr_per_kg : 1;
                  const min = rows.length > 0 ? rows[rows.length - 1].avg_fob_inr_per_kg : 0;
                  const range = Math.max(top - min, 1);
                  if (rows.length === 0) {
                    return <div className="text-xs text-dash-mut py-4">No data for current filters.</div>;
                  }
                  return rows.map((b) => {
                    // Bar width: normalize between min and top (12% floor for visibility)
                    const pct = Math.max(12, ((b.avg_fob_inr_per_kg - min) / range) * 100);
                    return (
                      <div key={b.consignee} className="flex items-center gap-3 text-xs">
                        <div className="w-36 font-semibold truncate text-dash-ink flex items-center gap-1.5" title={b.consignee}>
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: b.color }} />
                          <span className="truncate">{b.consignee}</span>
                        </div>
                        <div className="flex-1 bg-dash-bg rounded-sm h-4 overflow-hidden relative border border-dash-line">
                          <div
                            className="h-full transition-all duration-500 rounded-sm"
                            style={{ width: `${pct}%`, backgroundColor: b.color, opacity: 0.85 }}
                          />
                        </div>
                        <div className="w-24 text-right font-mono font-bold text-dash-amber">
                          ₹{b.avg_fob_inr_per_kg.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-dash-line flex justify-between items-center text-xs text-dash-mut font-mono">
              <span>Overall Avg FOB:</span>
              <span className="text-dash-amber font-bold text-sm">
                ₹{(analytics.overall_avg_fob || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / KG
              </span>
            </div>
          </div>

          {/* Price Trend Over Time (Bubble Scatter) */}
          <div className="lg:col-span-7 bg-dash-panel2 text-dash-ink p-6 rounded-xl border border-dash-line shadow-lg flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-baseline mb-1">
                <h2 className="text-lg font-bold text-dash-ink tracking-tight">
                  Price Trend <em className="text-dash-amber font-serif italic font-normal">over time</em>
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-dash-mut uppercase tracking-wider font-semibold">Line Item Level</span>
                  <button
                    onClick={() => setTrendFullscreen(true)}
                    title="View fullscreen"
                    aria-label="View chart fullscreen"
                    className="p-1.5 rounded-md text-dash-mut hover:text-dash-ink hover:bg-dash-line transition-colors"
                    data-testid="btn-trend-fullscreen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-dash-mut mb-4">
                FOB ₹/Kg by invoice date. Each bubble is a line item — sized by shipment volume, colored by buyer.
              </p>

              <div className="h-64 w-full">
                {(analytics.price_trend && analytics.price_trend.length > 0) ? (() => {
                  // Build sorted list of unique dates and map each point to its category index
                  // This gives EVEN spacing across the x-axis (categorical), not time-proportional
                  const uniqueDates = [...new Set((analytics.price_trend || []).map(p => p.date))].sort();
                  const dateToIdx = Object.fromEntries(uniqueDates.map((d, i) => [d, i]));
                  const groups = {};
                  (analytics.price_trend || []).forEach((p) => {
                    if (!groups[p.consignee]) groups[p.consignee] = { color: p.color, data: [] };
                    groups[p.consignee].data.push({ ...p, date_idx: dateToIdx[p.date] });
                  });
                  const tickIndices = uniqueDates.map((_, i) => i);
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                        <CartesianGrid stroke="var(--dash-line)" strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          dataKey="date_idx"
                          domain={[-0.5, uniqueDates.length - 0.5]}
                          ticks={tickIndices}
                          tickFormatter={(i) => uniqueDates[i] || ''}
                          tick={{ fontSize: 10, fill: 'var(--dash-mut)' }}
                          stroke="var(--dash-line)"
                          angle={-30}
                          textAnchor="end"
                          height={55}
                          interval={0}
                          allowDecimals={false}
                        />
                        <YAxis
                          type="number"
                          dataKey="price_inr_per_kg"
                          tick={{ fontSize: 10, fill: 'var(--dash-mut)' }}
                          stroke="var(--dash-line)"
                          domain={[450, 800]}
                          ticks={[450, 500, 550, 600, 650, 700, 750, 800]}
                          allowDataOverflow={false}
                          tickFormatter={(v) => `₹${Math.round(v)}`}
                        />
                        <ZAxis type="number" dataKey="volume_kg" range={[30, 400]} />
                        <Tooltip
                          cursor={{ strokeDasharray: '3 3' }}
                          content={<PriceTrendTooltip />}
                        />
                        {Object.entries(groups).map(([consignee, g]) => (
                          <Scatter key={consignee} name={consignee} data={g.data} fill={g.color} fillOpacity={0.75} />
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  );
                })() : (
                  <div className="w-full h-full flex items-center justify-center text-dash-mut text-sm">
                    No data for current filters.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-dash-mut font-medium">
              {(analytics.buyer_ranking || []).map((b) => (
                <div key={b.consignee} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: b.color }} />
                  <span>{b.consignee}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
            {/* Search */}
            <div className="relative w-full lg:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-dash-mut" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Invoice, Consignee, PO, Container..."
                className="w-full pl-9 pr-4 py-2 border border-dash-line rounded-lg text-sm text-dash-ink bg-dash-panel2/50 focus:outline-none focus:ring-2 focus:ring-dash-ice focus:border-transparent focus:bg-dash-panel"
              />
            </div>

            {/* Consignee + Market */}
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              <div className="flex items-center gap-2 text-xs font-semibold text-dash-mut">
                <Building className="w-4 h-4 text-dash-ice" />
                <span>Consignee:</span>
              </div>
              <select
                value={consigneeFilter}
                onChange={(e) => setConsigneeFilter(e.target.value)}
                className="border border-dash-line bg-dash-panel2 rounded-lg px-3 py-2 text-xs font-medium text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
              >
                <option value="all">All Consignees</option>
                {consignees.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>

              <div className="flex gap-1 bg-dash-panel2 p-1 rounded-lg text-sm font-medium">
                {[
                  { id: 'all', label: 'All Markets' },
                  { id: 'USA', label: 'USA (USD)' },
                  { id: 'Russia', label: 'Russia (INR)' },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setCountryTab(t.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                      countryTab === t.id
                        ? 'text-dash-ink bg-dash-panel shadow-sm'
                        : 'text-dash-mut hover:text-dash-ink'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-dash-panel rounded-xl border border-dash-line shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-dash-panel2 border-b border-dash-line text-xs font-semibold text-dash-mut uppercase tracking-wider">
                  <th className="p-4">Invoice No & Date</th>
                  <th className="p-4">Consignee & Port</th>
                  <th className="p-4">Payment Terms</th>
                  <th className="p-4">Net Weight</th>
                  <th className="p-4 bg-[rgba(242,177,76,0.08)] text-dash-amber border-x border-dash-line">Price / KG</th>
                  <th className="p-4">
                    Invoice Amount
                    <span className="text-[10px] text-dash-mut font-normal block">(Incl. 5% GST where applicable)</span>
                  </th>
                  <th className="p-4 text-center">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dash-line text-sm text-dash-ink">
                {error && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-dash-coral">Error: {error}</td>
                  </tr>
                )}
                {!error && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-dash-mut">No invoices match your filters.</td>
                  </tr>
                )}
                {!error && filtered.map((inv) => (
                  <tr key={inv.invoice_no} className="hover:bg-dash-panel2 transition-colors">
                    <td className="p-4">
                      <div className="font-bold text-dash-ink">{inv.invoice_no}</div>
                      <div className="text-xs text-dash-mut font-mono">{inv.date}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-semibold text-dash-ink">{inv.consignee}</div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold mt-0.5 ${countryBadgeClass(inv.country)}`}>
                        {inv.port}
                      </span>
                    </td>
                    <td className="p-4 text-xs font-medium text-dash-ink max-w-xs">
                      <span className="bg-dash-panel2 text-dash-ink px-2 py-1 rounded inline-block border border-dash-line">
                        {inv.payment_terms}
                      </span>
                    </td>
                    <td className="p-4 text-dash-mut font-medium">{formatWeight(inv.net_weight, inv.currency)}</td>
                    <td className="p-4 bg-[rgba(242,177,76,0.06)] border-x border-dash-line font-bold text-dash-amber font-mono">{inv.price_per_kg}</td>
                    <td className="p-4">
                      <div className="font-bold text-dash-ink font-mono">{inv.invoice_amount}</div>
                      <div className="text-[10px] text-dash-mut font-medium">{inv.tax_note}</div>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => openModal(inv)}
                        title="View Invoice & Proforma Invoice"
                        className="text-xs font-bold text-dash-ice hover:text-white hover:bg-dash-ice border border-dash-ice/30 bg-[rgba(79,195,201,0.1)] px-3 py-1.5 rounded-lg transition-all inline-flex items-center gap-1.5 shadow-sm"
                        data-testid={`view-docs-${inv.invoice_num}`}
                      >
                        <Eye className="w-4 h-4" />
                        View Docs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Fullscreen Price Trend Overlay */}
      {trendFullscreen && (analytics.price_trend && analytics.price_trend.length > 0) && (() => {
        const uniqueDates = [...new Set((analytics.price_trend || []).map(p => p.date))].sort();
        const dateToIdx = Object.fromEntries(uniqueDates.map((d, i) => [d, i]));
        const groups = {};
        (analytics.price_trend || []).forEach((p) => {
          if (!groups[p.consignee]) groups[p.consignee] = { color: p.color, data: [] };
          groups[p.consignee].data.push({ ...p, date_idx: dateToIdx[p.date] });
        });
        const tickIndices = uniqueDates.map((_, i) => i);
        return (
          <div
            className="fixed inset-0 z-50 bg-dash-bg/95 backdrop-blur-sm flex flex-col p-6"
            onKeyDown={(e) => { if (e.key === 'Escape') setTrendFullscreen(false); }}
          >
            {/* Fullscreen header */}
            <div className="flex justify-between items-center mb-4 shrink-0">
              <div>
                <h2 className="text-2xl font-bold text-dash-ink tracking-tight">
                  Price Trend <em className="text-dash-amber font-serif italic font-normal">over time</em>
                </h2>
                <p className="text-sm text-dash-mut mt-1">
                  FOB ₹/Kg by invoice date. Each bubble is a line item — sized by shipment volume, colored by buyer.
                </p>
              </div>
              <button
                onClick={() => setTrendFullscreen(false)}
                title="Exit fullscreen (Esc)"
                className="p-2 rounded-lg text-dash-mut hover:text-dash-ink hover:bg-dash-panel2 transition-colors border border-dash-line"
                data-testid="btn-trend-fullscreen-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Fullscreen chart */}
            <div className="flex-1 min-h-0 bg-dash-panel2 border border-dash-line rounded-xl p-6 shadow-2xl flex flex-col">
              <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 40, bottom: 30, left: 20 }}>
                    <CartesianGrid stroke="var(--dash-line)" strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="date_idx"
                      domain={[-0.5, uniqueDates.length - 0.5]}
                      ticks={tickIndices}
                      tickFormatter={(i) => uniqueDates[i] || ''}
                      tick={{ fontSize: 12, fill: 'var(--dash-mut)' }}
                      stroke="var(--dash-line)"
                      angle={-30}
                      textAnchor="end"
                      height={70}
                      interval={0}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="number"
                      dataKey="price_inr_per_kg"
                      tick={{ fontSize: 12, fill: 'var(--dash-mut)' }}
                      stroke="var(--dash-line)"
                      domain={[450, 800]}
                      ticks={[450, 500, 550, 600, 650, 700, 750, 800]}
                      allowDataOverflow={false}
                      tickFormatter={(v) => `₹${Math.round(v)}`}
                    />
                    <ZAxis type="number" dataKey="volume_kg" range={[60, 900]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={<PriceTrendTooltip />}
                    />
                    {Object.entries(groups).map(([consignee, g]) => (
                      <Scatter key={consignee} name={consignee} data={g.data} fill={g.color} fillOpacity={0.75} />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-4 text-xs text-dash-mut font-medium shrink-0">
                {(analytics.buyer_ranking || []).map((b) => (
                  <div key={b.consignee} className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: b.color }} />
                    <span>{b.consignee}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal */}
      {selected && (
        <>
          <div
            className="fixed inset-0 bg-dash-bg/70 backdrop-blur-sm z-40 transition-opacity"
            onClick={closeModal}
          />
          <div className="fixed inset-y-4 inset-x-4 md:inset-x-12 bg-dash-panel rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-dash-line bg-dash-panel2">
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-1 rounded text-xs font-bold ${countryBadgeClass(selected.country)}`}>
                  {selected.country}
                </span>
                <div>
                  <h2 className="text-lg font-bold text-dash-ink">{selected.invoice_no}</h2>
                  <p className="text-xs text-dash-mut">Date: {selected.date} · {selected.port}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold bg-dash-panel border border-dash-line text-dash-ink px-3 py-1.5 rounded-lg hover:bg-dash-panel2 flex items-center gap-1.5"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Selected PDF
                  </a>
                )}
                <button
                  onClick={closeModal}
                  className="p-1.5 text-dash-mut hover:text-dash-ink rounded-lg hover:bg-dash-line"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden bg-dash-bg">
              {/* Left: PDF viewer */}
              <div className="lg:col-span-7 border-r border-dash-line bg-dash-panel2 p-3 flex flex-col h-full space-y-2">
                <div className="flex items-center justify-between bg-dash-bg/60 p-1.5 rounded-lg border border-dash-line text-xs">
                  <span className="text-dash-mut font-medium px-2 flex items-center gap-1">
                    <Files className="w-3.5 h-3.5" />
                    Select Document:
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setDocTab('invoice')}
                      className={`px-3 py-1.5 rounded-md font-semibold transition-all flex items-center gap-1.5 shadow-sm ${
                        docTab === 'invoice'
                          ? 'text-white bg-dash-ice'
                          : 'text-dash-mut hover:text-dash-ink hover:bg-dash-line'
                      }`}
                      disabled={!selected.has_invoice_pdf}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Commercial Invoice
                    </button>
                    <button
                      onClick={() => setDocTab('proforma')}
                      className={`px-3 py-1.5 rounded-md font-semibold transition-all flex items-center gap-1.5 ${
                        docTab === 'proforma'
                          ? 'text-white bg-dash-ice shadow-sm'
                          : 'text-dash-mut hover:text-dash-ink hover:bg-dash-line'
                      }`}
                      disabled={!selected.has_proforma_pdf}
                    >
                      <FileCheck className="w-3.5 h-3.5" />
                      Proforma Invoice (PI)
                    </button>
                  </div>
                </div>

                <div className="w-full flex-1 rounded-lg bg-dash-panel border-0 shadow-inner overflow-hidden">
                  {pdfUrl ? (
                    <iframe
                      key={pdfUrl}
                      src={pdfUrl}
                      title={`${selected.invoice_no} - ${docTab}`}
                      className="w-full h-full border-0"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-dash-mut text-sm">
                      No {docTab === 'invoice' ? 'Commercial Invoice' : 'Proforma Invoice'} available.
                    </div>
                  )}
                </div>
              </div>

              {/* Right: details */}
              <div className="lg:col-span-5 p-6 bg-dash-panel overflow-y-auto space-y-6 flex flex-col justify-between">
                <div className="space-y-6">
                  {/* Shipping & Document References */}
                  <div>
                    <h4 className="text-xs font-bold text-dash-mut uppercase tracking-wider mb-2">
                      Shipping & Document References
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-xs bg-dash-panel2 p-3.5 rounded-xl border border-dash-line">
                      <div>
                        <p className="text-dash-mut">Consignee</p>
                        <p className="font-bold text-dash-ink mt-0.5">{selected.consignee}</p>
                      </div>
                      <div>
                        <p className="text-dash-mut">Discharge Port</p>
                        <p className="font-bold text-dash-ink mt-0.5">{selected.port}</p>
                      </div>
                      {selected.pi_po_ref && (
                        <div className="col-span-2">
                          <p className="text-dash-mut">Proforma Invoice / PO Ref</p>
                          <p className="font-bold text-dash-ice bg-[rgba(79,195,201,0.1)] px-2.5 py-1 rounded border border-dash-ice/30 mt-1 inline-flex items-center gap-1.5">
                            <FileCheck className="w-3.5 h-3.5" />
                            {selected.pi_po_ref}
                          </p>
                        </div>
                      )}
                      <div className="col-span-2">
                        <p className="text-dash-mut">Payment Terms</p>
                        <p className="font-bold text-dash-purple bg-[rgba(185,140,214,0.1)] px-2.5 py-1 rounded border border-dash-purple/30 mt-1 inline-block">
                          {selected.payment_terms}
                        </p>
                      </div>
                      {selected.container && (
                        <div>
                          <p className="text-dash-mut">Container</p>
                          <p className="font-mono text-dash-ink mt-0.5">{selected.container}</p>
                        </div>
                      )}
                      {selected.seal_no && (
                        <div>
                          <p className="text-dash-mut">Seal No.</p>
                          <p className="font-mono text-dash-ink mt-0.5">{selected.seal_no}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Line Items */}
                  <div>
                    <h4 className="text-xs font-bold text-dash-mut uppercase tracking-wider mb-2">
                      Line Item & Rate Breakdown
                    </h4>
                    <div className="space-y-2">
                      {(selected.line_items && selected.line_items.length > 0) ? (
                        selected.line_items.map((li, idx) => (
                          <div key={idx} className="bg-dash-panel border border-dash-line rounded-lg p-3 flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-dash-ink text-[13px] leading-snug">{li.description}</p>
                              <p className="text-[11px] text-dash-mut mt-1">
                                Qty: <span className="font-medium text-dash-ink">{li.qty}</span>
                                <span className="mx-1.5 text-dash-line">|</span>
                                Rate: <span className="font-bold text-dash-amber">{li.rate}</span>
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-bold text-dash-ink text-sm whitespace-nowrap">{li.amount}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="border border-dash-line rounded-lg overflow-hidden">
                          <div className="bg-dash-panel2 px-3 py-2 flex justify-between text-[11px] font-semibold text-dash-mut uppercase tracking-wider">
                            <span>Description</span>
                            <span>Value</span>
                          </div>
                          <div className="divide-y divide-dash-line text-xs">
                            <div className="px-3 py-2 flex justify-between">
                              <span className="text-dash-mut">Net Weight</span>
                              <span className="font-medium text-dash-ink">{formatWeight(selected.net_weight, selected.currency)}</span>
                            </div>
                            <div className="px-3 py-2 flex justify-between bg-[rgba(242,177,76,0.06)]">
                              <span className="text-dash-amber font-medium">Price / KG</span>
                              <span className="font-bold text-dash-amber">{selected.price_per_kg}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* GST / Freight / FOB */}
                  {(selected.gst_component || selected.freight_amount || selected.fob_value) && (
                    <div className="border-t border-dash-line pt-3 space-y-1.5 text-xs">
                      {selected.gst_component && (
                        <div className="flex justify-between text-dash-purple bg-[rgba(185,140,214,0.1)] p-2 rounded font-medium">
                          <span>IGST Component (@ 5%):</span>
                          <span>{selected.gst_component}</span>
                        </div>
                      )}
                      {selected.freight_amount && (
                        <div className="flex justify-between text-dash-mut px-2">
                          <span>Freight Amount:</span>
                          <span className="font-medium">{selected.freight_amount}</span>
                        </div>
                      )}
                      {selected.fob_value_inr && (
                        <div className="flex justify-between text-dash-mut px-2">
                          <span>FOB Value (INR Equivalent):</span>
                          <span className="font-bold text-dash-ink">{selected.fob_value_inr}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="border-t border-dash-line pt-4 mt-4 flex justify-between items-center">
                  <div>
                    <span className="text-sm font-semibold text-dash-mut block">Total Invoice Amount</span>
                    <span className={`text-[11px] font-medium ${selected.tax_note?.includes('IGST') ? 'text-dash-purple' : 'text-dash-green'}`}>
                      {selected.tax_note?.includes('IGST') ? 'Inclusive of 5% IGST' : 'Zero-rated Export / Direct Exemption'}
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-dash-ice">{selected.invoice_amount}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <FloatingFAB guideContent={GUIDE} />
    </div>
  );
};

export default InvoicePITrackerPage;
